import { __awaiter, __generator } from "tslib";
import { Kind, isSchema, print } from 'graphql';
import { debugLog, asArray, isValidPath, parseGraphQLSDL, printSchemaWithDirectives, } from '@graphql-tools/utils';
import { gqlPluckFromCodeString, gqlPluckFromCodeStringSync, } from '@graphql-tools/graphql-tag-pluck';
import { tryToLoadFromExport, tryToLoadFromExportSync } from './load-from-module';
import { isAbsolute, resolve } from 'path';
import { cwd } from 'process';
import { readFileSync, accessSync, promises as fsPromises } from 'fs';
var readFile = fsPromises.readFile, access = fsPromises.access;
var FILE_EXTENSIONS = ['.ts', '.tsx', '.js', '.jsx', '.vue'];
/**
 * This loader loads GraphQL documents and type definitions from code files
 * using `graphql-tag-pluck`.
 *
 * ```js
 * const documents = await loadDocuments('queries/*.js', {
 *   loaders: [
 *     new CodeFileLoader()
 *   ]
 * });
 * ```
 *
 * Supported extensions include: `.ts`, `.tsx`, `.js`, `.jsx`, `.vue`
 */
var CodeFileLoader = /** @class */ (function () {
    function CodeFileLoader() {
    }
    CodeFileLoader.prototype.loaderId = function () {
        return 'code-file';
    };
    CodeFileLoader.prototype.canLoad = function (pointer, options) {
        return __awaiter(this, void 0, void 0, function () {
            var normalizedFilePath, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!isValidPath(pointer)) return [3 /*break*/, 4];
                        if (!FILE_EXTENSIONS.find(function (extension) { return pointer.endsWith(extension); })) return [3 /*break*/, 4];
                        normalizedFilePath = isAbsolute(pointer) ? pointer : resolve(options.cwd || cwd(), pointer);
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, access(normalizedFilePath)];
                    case 2:
                        _b.sent();
                        return [2 /*return*/, true];
                    case 3:
                        _a = _b.sent();
                        return [2 /*return*/, false];
                    case 4: return [2 /*return*/, false];
                }
            });
        });
    };
    CodeFileLoader.prototype.canLoadSync = function (pointer, options) {
        if (isValidPath(pointer)) {
            if (FILE_EXTENSIONS.find(function (extension) { return pointer.endsWith(extension); })) {
                var normalizedFilePath = isAbsolute(pointer) ? pointer : resolve(options.cwd || cwd(), pointer);
                try {
                    accessSync(normalizedFilePath);
                    return true;
                }
                catch (_a) {
                    return false;
                }
            }
        }
        return false;
    };
    CodeFileLoader.prototype.load = function (pointer, options) {
        return __awaiter(this, void 0, void 0, function () {
            var normalizedFilePath, errors, content, sdl, e_1, loaded, source, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        normalizedFilePath = ensureAbsolutePath(pointer, options);
                        errors = [];
                        if (!!options.noPluck) return [3 /*break*/, 5];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, readFile(normalizedFilePath, { encoding: 'utf-8' })];
                    case 2:
                        content = _a.sent();
                        return [4 /*yield*/, gqlPluckFromCodeString(normalizedFilePath, content, options.pluckConfig)];
                    case 3:
                        sdl = _a.sent();
                        if (sdl) {
                            return [2 /*return*/, parseSDL({ pointer: pointer, sdl: sdl, options: options })];
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        e_1 = _a.sent();
                        debugLog("Failed to load schema from code file \"" + normalizedFilePath + "\": " + e_1.message);
                        errors.push(e_1);
                        return [3 /*break*/, 5];
                    case 5:
                        if (!!options.noRequire) return [3 /*break*/, 11];
                        _a.label = 6;
                    case 6:
                        _a.trys.push([6, 10, , 11]);
                        if (!(options && options.require)) return [3 /*break*/, 8];
                        return [4 /*yield*/, Promise.all(asArray(options.require).map(function (m) { return import(m); }))];
                    case 7:
                        _a.sent();
                        _a.label = 8;
                    case 8: return [4 /*yield*/, tryToLoadFromExport(normalizedFilePath)];
                    case 9:
                        loaded = _a.sent();
                        source = resolveSource(pointer, loaded, options);
                        if (source) {
                            return [2 /*return*/, source];
                        }
                        return [3 /*break*/, 11];
                    case 10:
                        e_2 = _a.sent();
                        errors.push(e_2);
                        return [3 /*break*/, 11];
                    case 11:
                        if (errors.length > 0) {
                            throw errors[0];
                        }
                        return [2 /*return*/, null];
                }
            });
        });
    };
    CodeFileLoader.prototype.loadSync = function (pointer, options) {
        var normalizedFilePath = ensureAbsolutePath(pointer, options);
        var errors = [];
        if (!options.noPluck) {
            try {
                var content = readFileSync(normalizedFilePath, { encoding: 'utf-8' });
                var sdl = gqlPluckFromCodeStringSync(normalizedFilePath, content, options.pluckConfig);
                if (sdl) {
                    return parseSDL({ pointer: pointer, sdl: sdl, options: options });
                }
            }
            catch (e) {
                debugLog("Failed to load schema from code file \"" + normalizedFilePath + "\": " + e.message);
                errors.push(e);
            }
        }
        if (!options.noRequire) {
            try {
                if (options && options.require) {
                    asArray(options.require).forEach(function (m) { return require(m); });
                }
                var loaded = tryToLoadFromExportSync(normalizedFilePath);
                var source = resolveSource(pointer, loaded, options);
                if (source) {
                    return source;
                }
            }
            catch (e) {
                errors.push(e);
            }
        }
        if (errors.length > 0) {
            throw errors[0];
        }
        return null;
    };
    return CodeFileLoader;
}());
export { CodeFileLoader };
function parseSDL(_a) {
    var pointer = _a.pointer, sdl = _a.sdl, options = _a.options;
    return parseGraphQLSDL(pointer, sdl, options);
}
function resolveSource(pointer, value, options) {
    if (isSchema(value)) {
        return {
            location: pointer,
            rawSDL: printSchemaWithDirectives(value, options),
            schema: value,
        };
    }
    else if ((value === null || value === void 0 ? void 0 : value.kind) === Kind.DOCUMENT) {
        return {
            location: pointer,
            rawSDL: print(value),
            document: value,
        };
    }
    else if (typeof value === 'string') {
        return parseGraphQLSDL(pointer, value, options);
    }
    return null;
}
function ensureAbsolutePath(pointer, options) {
    return isAbsolute(pointer) ? pointer : resolve(options.cwd || cwd(), pointer);
}
//# sourceMappingURL=index.js.map